import ReactGA4 from 'react-ga4';

/**
 * GA4 analytics wrapper (over react-ga4).
 *
 * Exposes the same method names the codebase already used with `react-ga`
 * (Universal Analytics) so call sites don't need to change their bodies — only
 * their import (`react-ga` -> `Shared/analytics`).
 *
 * react-ga (UA) was silently dropped by our GA4 property; react-ga4 sends real
 * GA4 events. The Measurement ID comes from `process.env.GA_CODE` (injected per
 * deployment in .gitlab-ci.yml), NOT from the Firebase SDK at runtime.
 */

let initialized = false;

const initialize = (measurementId, options = {}) => {
  if (!measurementId) return; // no-op when GA_CODE is unset (e.g. local dev)
  ReactGA4.initialize(measurementId, options);
  initialized = true;
};

const isReady = () => initialized;

// react-ga: pageview(path, title) -> react-ga4 send()
const pageview = (path, title) => {
  if (!initialized) return;
  ReactGA4.send({ hitType: 'pageview', page: path, title });
};

// Same {category, action, label} signature as react-ga.
const event = (params) => {
  if (!initialized) return;
  ReactGA4.event(params);
};

// react-ga4 has no `.exception`; model it as an `exception` event.
const exception = ({ description, fatal } = {}) => {
  if (!initialized) return;
  ReactGA4.event('exception', { description, fatal });
};

// react-ga4 has no `.outboundLink`; emit an outbound click then run the callback.
const outboundLink = ({ label } = {}, hitCallback) => {
  if (initialized) {
    ReactGA4.event('click', { label, outbound: true });
  }
  if (typeof hitCallback === 'function') hitCallback();
};

const set = (fields) => {
  if (!initialized) return;
  ReactGA4.set(fields);
};

// Sets persistent params (custom dimensions) on every subsequent event,
// e.g. setContext({ app_area: 'employee', user_role: 'manager' }).
const setContext = (params) => {
  if (!initialized) return;
  ReactGA4.gtag('set', params);
};

const ReactGA = {
  initialize,
  isReady,
  pageview,
  event,
  exception,
  outboundLink,
  set,
  setContext,
};

export default ReactGA;
